import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import { toast } from "sonner";

interface CreateWorkspaceData {
  name: string;
}

export function useCreateWorkspace() {
  const { user } = useAuth();
  const { refreshWorkspaces, switchWorkspace } = useWorkspace();
  const [isLoading, setIsLoading] = useState(false);

  const createWorkspace = async (data: CreateWorkspaceData) => {
    if (!user) {
      toast.error("You must be logged in to create a workspace");
      return null;
    }

    try {
      setIsLoading(true);

      // Create the workspace
      const { data: workspace, error: workspaceError } = await supabase
        .from("workspaces")
        .insert({
          name: data.name,
          created_by_user_id: user.id,
          slug: `ws-${Date.now().toString(36)}`,
        })
        .select()
        .single();

      if (workspaceError) throw workspaceError;

      // Add user as owner
      const { error: memberError } = await supabase
        .from("workspace_members")
        .insert({
          workspace_id: workspace.id,
          user_id: user.id,
          role: "owner",
          status: "active",
        });

      if (memberError) throw memberError;

      // seed_workspace_feature_flags + seed_workspace_default_pipelines are
      // both Postgres RPCs added in recent migrations; they predate the
      // current generated `Database` types union. Cast once so the typechecker
      // doesn't reject the names until the user runs `pnpm supabase gen types`.
      //
      // Must stay bound to the client: `SupabaseClient.rpc` is a prototype
      // method whose body is `return this.rest.rpc(...)`. A detached reference
      // (`const f = supabase.rpc`) calls it with `this === undefined` and
      // throws "Cannot read properties of undefined (reading 'rest')" before
      // any request goes out — which aborted creation *after* the workspace and
      // membership rows were already inserted, leaving a workspace with no
      // feature flags (every module gated off → blank page).
      const rpcAny = supabase.rpc.bind(supabase) as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ error: { message: string } | null }>;

      // Seed default feature flags (all modules enabled) for the new workspace.
      const { error: flagsError } = await rpcAny(
        "seed_workspace_feature_flags",
        { p_workspace_id: workspace.id },
      );
      if (flagsError) throw flagsError;

      // Seed the workspace's default pipelines (stages) + their default
      // checklist items. Failure here is logged but doesn't abort workspace
      // creation — the user can still manage pipelines via the Pipeline UI.
      // The seed RPC is in migration 20260526100400_seed_workspace_default_pipelines.sql.
      const { error: pipelinesError } = await rpcAny(
        "seed_workspace_default_pipelines",
        { p_workspace_id: workspace.id, p_created_by: user.id },
      );
      if (pipelinesError) {
        console.error(
          "seed_workspace_default_pipelines failed:",
          pipelinesError,
        );
      }

      toast.success(`Workspace "${data.name}" created!`);

      // Refresh and switch to new workspace
      await refreshWorkspaces();
      switchWorkspace(workspace.id);

      return workspace;
    } catch (err) {
      console.error("Error creating workspace:", err);
      // Include the underlying reason — a bare "Failed to create workspace"
      // hides whether the insert, the RLS policy, or a seeding RPC was at fault.
      const reason =
        err instanceof Error
          ? err.message
          : typeof err === "object" && err !== null && "message" in err
            ? String((err as { message: unknown }).message)
            : "";
      toast.error(
        reason
          ? `Failed to create workspace: ${reason}`
          : "Failed to create workspace",
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { createWorkspace, isLoading };
}
